import { memo, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Marker } from '@adamscybot/react-leaflet-component-marker';
import { Tooltip } from '@ds';
import { MapPointIcon } from '@icons';
import { ContractorSurgeonMarkerType, MapFilterType, SurgeonMarkerType } from '@lp/features/RoutePlanner/types';
import { MARKER_TYPE_ICONS } from '@lp/features/RoutePlanner/config';
import { getIconMapColor } from '@lp/features/RoutePlanner/utils';
import { ConflictReservationInfo, MatchCell, OrgansLastCall } from '@lp/features/AssignmentTables/components';
import { formatHomeLocation } from '@lp/features/AssignmentTables/SurgeonsTable/utils';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { CaseMatchType, RSAssignmentCandidate } from '@/api/apis/resources';
import { SegmentTypeEnum } from '@/api/apis/route';
import { ResourceName } from './ResourceName';

export const SurgeonMarker = memo(
  ({
    location,
    item,
    isContractor,
    showTooltip = false,
  }: (SurgeonMarkerType | ContractorSurgeonMarkerType) & { showTooltip?: boolean }) => {
    const { caseName } = useCaseDetails();
    const [isOpen, setIsOpen] = useState(false);
    const color = getIconMapColor(SegmentTypeEnum.custom);
    const homeLocationString = formatHomeLocation(item.homeLocation);
    const title = isContractor ? '(Contractor Retrieval Surgeon)' : '(Retrieval Surgeon)';
    const isReserved = item.match.caseType !== CaseMatchType.AssignmentCandidate;
    const shiftCertificates = useMemo(() => {
      return isContractor ? item.employeeCertificates : (item as RSAssignmentCandidate).shiftCertificates;
    }, [isContractor, item]);

    const icon = useMemo(() => {
      const surgeonIcon = (
        <div className="py-2 flex flex-col gap-2">
          <div className="flex gap-1">
            <span className="text-m-sb text-black">{homeLocationString}</span>
            <span className="text-m-r text-black">{title}</span>
          </div>
          <div
            key={item.employeeId}
            className={classnames('flex gap-2 items-center py-1 px-2 rounded-lg', {
              'opacity-70': isReserved,
            })}
          >
            <ResourceName fullName={item.employeeFullName} photoPath={item.employeeProfilePhoto} />
            <OrgansLastCall
              shiftCertificates={shiftCertificates}
              employeeCertificates={item.employeeCertificates}
              lastCallDaysByOrgan={item.lastCallDays.forCaseDonorByOrgan}
              size="sm"
            />
            {!isContractor && (
              <div className="ml-auto flex gap-2">
                <MatchCell matchType={item.match.matchType} />
                <ConflictReservationInfo
                  reservation={item.conflictedReservation}
                  caseTypeMatch={item.match.caseType}
                  caseName={caseName}
                  isColumn
                />
              </div>
            )}
          </div>
        </div>
      );

      return showTooltip ? (
        <Tooltip
          placement="bottom"
          onOpenChange={(value) => setIsOpen(value)}
          showArrow
          closeDelay={0}
          classNames={{
            base: 'group',
            content: 'shadow-xl opacity-90',
          }}
          content={surgeonIcon}
        >
          <div>
            <div className="relative flex flex-col justify-center items-center mt-3">
              <div className="relative text-base-25 mt-0.5 overflow-hidden" style={{ color }}>
                <MapPointIcon />
                <span className="w-full font-secondary text-center absolute top-[6px] left-[6px] text-base-90 text-s-sb">
                  <div className="box-border w-3 h-3 rounded-full bg-white" style={{ borderColor: color }} />
                </span>
              </div>
              <div className="box-border w-3 h-3 rounded-full border-3 bg-white" style={{ borderColor: color }} />
            </div>
            <div
              className={`w-max rounded-3xl py-1.5 px-2 flex gap-2 items-center text-base-5 shadow-xl hover:bg-base-60 ${isOpen ? 'bg-base-60' : 'bg-white'}`}
            >
              {MARKER_TYPE_ICONS[MapFilterType.surgeon]}
              <span
                className={`text-s-sb ${isReserved ? 'block bg-base-70 px-1 text-s-sb text-base-30 rounded-md' : ''}`}
              >
                1
              </span>
            </div>
          </div>
        </Tooltip>
      ) : (
        surgeonIcon
      );
    }, [
      showTooltip,
      isOpen,
      color,
      homeLocationString,
      title,
      item,
      isReserved,
      shiftCertificates,
      isContractor,
      caseName,
    ]);

    return (
      <Marker
        position={location}
        zIndexOffset={10}
        componentIconOpts={{
          disableClickPropagation: true,
        }}
        icon={icon}
      />
    );
  },
);
